<template>
    <div class="news-manage-content">
        <div class="add-button">
            <el-button type="primary" @click="onAdd">新增新闻</el-button>
        </div>

        <div class="table">
            <div class="title">新闻管理</div>


            <el-table :data="tableData" style="width: 100%">
                <el-table-column prop="order" label="序号" />
                <el-table-column prop="title" label="标题" />
                <el-table-column prop="author" label="作者" />
                <el-table-column prop="createTime" label="发布时间" />
                <el-table-column prop="updateTime" label="更新时间" />
                <el-table-column label="操作">
                    <template #default="{ row }">
                        <el-button size="small" type="primary"
                            @click="setRuleFormValue(row.id, true, 'qeury')">详情</el-button>
                        <el-button size="small" type="success"
                            @click="setRuleFormValue(row.id, false, 'update')">修改</el-button>
                        <el-button size="small" type="danger" @click="onDeleteHandler(row.id)">
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <div class="page">
            <Pagination :page-size="pageSize" v-model:current-page="currentPage" :total-page="totalPage"
                @current-page-change="pageChange"></Pagination>
        </div>
    </div>

    <el-drawer v-model="isOpendrawer" title="新增新闻" size="50%" @closed="onDrawerClosed">
        <el-form ref="ruleFormRef" style="max-width: 600px" :model="ruleForm" :rules="disabled ? null : rules"
            label-width="auto" :disabled="disabled">
            <el-form-item label="新闻标题" prop="title">
                <el-input v-model="ruleForm.title" />
            </el-form-item>

            <el-form-item label="作者" prop="author">
                <el-input v-model="ruleForm.author" />
            </el-form-item>

            <el-form-item label="新闻内容" prop="content">
                <Editor ref="editorRef" v-model:value-html="valueHtml" :mode="'default'" :placeholder="'请输入新闻内容'"
                    @on-change="onChange" :disabled="disabled">
                </Editor>
            </el-form-item>

            <el-form-item>
                <el-button type="primary" @click="submitForm(ruleFormRef)" v-if="formType === 'add'">
                    提交
                </el-button>
                <el-button type="success" @click="onUpdateNewsHandler(ruleFormRef)" v-else-if="formType === 'update'">
                    更新
                </el-button>
            </el-form-item>
        </el-form>
    </el-drawer>
</template>

<script lang="ts" setup>
import { nextTick, onBeforeUnmount, onMounted, Reactive, reactive, ref, Ref, shallowRef, watch } from 'vue';
import Pagination from '../Pagination.vue';
import { BaseResponse, GetNewsDetailsResponse, GetNewsListResponse } from '@/typeDefine/HttpResponseType';
import { Action, FormType } from '@/typeDefine/CommonTypes';
import { Api } from '@/http/Api';
import { UtilMethods } from '@/util/UtilMethods';
import { ResponseCode } from '@/http/ResponseCode';
import { ElMessageBox, FormInstance, FormRules } from 'element-plus';
import { NewsType } from '@/typeDefine/EntityType';
import Editor, { EditorComponentType } from './Editor.vue';

//当前页
const currentPage: Ref<number> = ref(1)
//页大小
const pageSize: Ref<number> = ref(10)
//总页数
const totalPage: Ref<number> = ref(0)

//是否打开弹窗
const isOpendrawer: Ref<boolean> = ref(false)

//表格内容
const tableData: Ref<GetNewsListResponse['data']['news']> = ref([])

const ruleFormRef: Ref<FormInstance | undefined> = ref()

//当前表单状态
const formType: Ref<FormType> = ref('none')

//当前选中的id
const currentNewsId: Ref<number> = ref(0)

/**
* 富文本相关字段
*/
const editorRef: Ref<EditorComponentType | null> = ref(null)
const valueHtml: Ref<string> = ref('')
const disabled: Ref<boolean> = ref(false)

const ruleForm: Reactive<Pick<NewsType, 'title' | 'author' | 'content'>> = reactive({
    title: '',
    content: '',
    author: ''
})

const rules: Reactive<FormRules<Pick<NewsType, 'title' | 'author' | 'content'>>> = reactive({
    title: [
        {
            required: true, message: '请输入标题', trigger: 'blur',
        },
        {
            max: 255, message: '最大输入255个字符', trigger: 'blur'
        }
    ],
    author: [
        {
            max: 255, message: '最大输入255个字符', trigger: 'blur'
        }
    ],
    content: [
        {
            required: true, message: '请输入内容', trigger: 'blur',
        },
    ]
})

onMounted(() => requestTableData(currentPage.value))

const requestTableData: Action<[number]> = async (currentPage: number) => {
    const response: GetNewsListResponse = await Api.instance.getNewsListByPage({ startPage: currentPage })

    UtilMethods.showMessageToast(response, false)
    if (response.code === ResponseCode.OK) {
        tableData.value = response.data.news
        totalPage.value = response.data.totalPage

        let i: number = 0
        tableData.value = tableData.value.map(item => {
            return {
                order: ++i,
                ...item
            }
        })

    }
}

const onAdd: Action = () => {
    isOpendrawer.value = true
    formType.value = 'add'
}

//当前页改变
const pageChange: Action<[number]> = (value: number) => {
    requestTableData(value)
}

//监听富文本的文字
const onChange: Action<[string]> = (value: string) => {
    ruleForm.content = value
}

//刷新
const refresh: Action = () => {
    currentPage.value = 1
    requestTableData(currentPage.value)
}

//抽屉关闭
const onDrawerClosed: Action = () => {
    disabled.value = false

    for (const item in ruleForm) {
        ruleForm[item as keyof typeof ruleForm] = ''
    }

    editorRef.value?.clear()
}

//提交新闻
const submitForm: Action<[FormInstance | undefined]> = async (formEl: FormInstance | undefined) => {
    if (!formEl) return
    await formEl.validate(async (valid, fields) => {

        if (!valid) {
            return
        }

        const response: BaseResponse = await Api.instance.addNews({
            title: ruleForm.title,
            author: ruleForm.author,
            content: valueHtml.value
        })

        UtilMethods.showMessageToast(response)

        if (response.code === ResponseCode.OK) {
            refresh()
        }
    })
}

//更新新闻
const onUpdateNewsHandler: Action<[FormInstance | undefined]> = async (formEl: FormInstance | undefined) => {
    if (!formEl) return
    await formEl.validate(async (valid, fields) => {

        if (!valid) {
            return
        }

        const response: BaseResponse = await Api.instance.updateNews({
            id: currentNewsId.value,
            title: ruleForm.title,
            author: ruleForm.author,
            content: valueHtml.value
        })

        UtilMethods.showMessageToast(response)

        if (response.code === ResponseCode.OK) {
            refresh()
        }
    })
}

//删除新闻
const onDeleteHandler: Action<[number]> = (id: number) => {
    ElMessageBox.confirm('将执行删除该新闻操作,是否确定?', '删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
    }).then(async () => {
        const response: BaseResponse = await Api.instance.deleteNews({
            id,
        })

        UtilMethods.showMessageToast(response)

        if (response.code === ResponseCode.OK) {
            refresh()
        }
    }).catch(() => { })
}

//将表单值设置成指定值
const setRuleFormValue: Action<[number, boolean, FormType]> = async (id: number, isDisabled: boolean, type: FormType) => {
    disabled.value = isDisabled
    isOpendrawer.value = true
    formType.value = type
    currentNewsId.value = id

    const response: GetNewsDetailsResponse = await Api.instance.getNewsDetails({ id })

    UtilMethods.showMessageToast(response, false)

    if (response.code === ResponseCode.OK) {
        const { author, title, content } = response.data.details
        ruleForm.author = author
        ruleForm.title = title

        editorRef.value?.setHtml(content)
    }
}
</script>

<style lang="css" scoped>
.news-manage-content .add-button {
    margin: 20px 0;
}

.news-manage-content .table .title {
    font-size: 20px;
    font-weight: bold;
}

.news-manage-content .page {
    display: flex;
    justify-content: right;
    align-items: center;
    margin: 0 20px;
}
</style>